import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './style.scss';
import Props from './typing';
import Lottie from 'react-lottie';
import animationData from '../../asset/lotties/loader.json';
import citiAnimationData from '../../asset/lotties/loaderCiti.json';
import { isCitiTheme } from 'lib/analyticsUtil';

let nonBlockingLoaderCounter = 0;
let activeNonBlockingLoaderIds: number[] = [];
const nonBlockingLoaderSubscribers = new Set<(activeId: number | null) => void>();

const getActiveNonBlockingLoaderId = (): number | null =>
  activeNonBlockingLoaderIds[activeNonBlockingLoaderIds.length - 1] ?? null;

const notifyNonBlockingLoaderSubscribers = (): void => {
  const activeId = getActiveNonBlockingLoaderId();
  nonBlockingLoaderSubscribers.forEach((subscriber) => subscriber(activeId));
};

const registerNonBlockingLoader = (
  id: number,
  subscriber: (activeId: number | null) => void
): (() => void) => {
  activeNonBlockingLoaderIds = activeNonBlockingLoaderIds.filter(
    (loaderId) => loaderId !== id
  );
  activeNonBlockingLoaderIds.push(id);
  nonBlockingLoaderSubscribers.add(subscriber);
  notifyNonBlockingLoaderSubscribers();

  return () => {
    activeNonBlockingLoaderIds = activeNonBlockingLoaderIds.filter(
      (loaderId) => loaderId !== id
    );
    nonBlockingLoaderSubscribers.delete(subscriber);
    notifyNonBlockingLoaderSubscribers();
  };
};

const Loader: FC<Props> = (props): JSX.Element | null => {
  const isCiti = isCitiTheme();
  const {
    message,
    variant = 'fullscreen',
    blocking = true,
    className = '',
    size,
    showMessage = true,
  } = props;
  const nonBlockingLoaderIdRef = useRef(0);
  const [activeNonBlockingLoaderId, setActiveNonBlockingLoaderId] = useState<
    number | null
  >(null);

  if (!nonBlockingLoaderIdRef.current) {
    nonBlockingLoaderCounter += 1;
    nonBlockingLoaderIdRef.current = nonBlockingLoaderCounter;
  }

  useLayoutEffect(() => {
    if (blocking || variant !== 'fullscreen') {
      return undefined;
    }

    return registerNonBlockingLoader(
      nonBlockingLoaderIdRef.current,
      setActiveNonBlockingLoaderId
    );
  }, [blocking, variant]);

  const loaderSize =
    size || (variant === 'inline' ? 18 : variant === 'section' ? 100 : 56);
  const loaderMessage = (message && message) || 'Loading';
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: isCiti ? citiAnimationData : animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  if (variant === 'inline') {
    return (
      <span
        className={`loader-inline ${className}`}
        role='status'
        aria-live='polite'
      >
        <Lottie
          options={defaultOptions}
          height={loaderSize}
          width={loaderSize}
        />
        {message && <span className='loader-inline__message'>{message}</span>}
      </span>
    );
  }

  if (variant === 'section') {
    return (
      <div
        className={`loader-section ${className}`}
        role='status'
        aria-live='polite'
      >
        <Lottie
          options={defaultOptions}
          height={loaderSize}
          width={loaderSize}
        />
        {showMessage && <h4>{loaderMessage}</h4>}
      </div>
    );
  }

  if (!blocking) {
    if (activeNonBlockingLoaderId !== nonBlockingLoaderIdRef.current) {
      return null;
    }

    const nonBlockingLoader = (
      <div
        className={`loader--non-blocking ${className}`}
        id='loader'
        role='status'
        aria-live='polite'
      >
        <div className='loader--non-blocking__content'>
          <div className='loader--non-blocking__body'>
            <Lottie
              options={defaultOptions}
              height={loaderSize}
              width={loaderSize}
            />
            {showMessage && <h4>{loaderMessage}</h4>}
          </div>
        </div>
      </div>
    );

    return ReactDOM.createPortal(nonBlockingLoader, document.body);
  }

  return (
    <React.Fragment>
      <div className='overlay'></div>
      <div
        className={`modal modal-dialog-centered loader ${className}`}
        id='loader'
        role='status'
        aria-live='polite'
      >
        <div className='modal-dialog'>
          <div className='modal-content'>
            <div className='modal-body'>
              <div>
                <Lottie
                  options={defaultOptions}
                  height={loaderSize}
                  width={loaderSize}
                />
              </div>
              {showMessage && <h4>{loaderMessage}</h4>}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Loader;
